/**
 * Admin > Integrations > Lab Uploads — partner lab-upload integrations for the
 * current practice. Backed by /v5/lab-upload-integrations (corp super admins
 * only; LabUploadIntegrationsController).
 *
 * A secret is only ever returned by create and regenerate, inside
 * `credentials`. List and read calls never carry it.
 */
import { apiClient } from './apiClient';
import type { ApiResponse } from '@/types/api.types';

export interface LabUploadIntegration {
  id: number;
  name: string;
  keyId: string;
  active: boolean;
  /** The same key may also ask whether a patient has labs on file (count + newest date only). */
  canCheck: boolean;
  noteTypeId: number;
  labTypeName: string;
  createdAt: string;
  lastUploadAt: string | null;
}

export interface LabUploadCredentials {
  keyId: string;
  secret: string;
  accountId: number;
  uploadUrl: string;
  /** Only for an integration that can check for labs. */
  checkUrl?: string;
}

export interface LabUploadLabType {
  id: number;
  name: string;
}

export type LabUploadOutcome = 'received' | 'filed' | 'duplicate' | 'not_matched' | 'ambiguous' | 'invalid' | 'error';

export interface LabUploadRequestRow {
  id: number;
  createdAt: string;
  outcome: LabUploadOutcome;
  integrationId: number | null;
  integrationName: string;
  patientId: number | null;
  patientName: string | null;
  labId: number | null;
  detail: string;
}

export interface LabUploadReadiness {
  labReadingReady: boolean;
  ambiguousGroups: number;
  ambiguousPatients: number;
  secretsConfigured: boolean;
}

const BASE = '/lab-upload-integrations';

export function listLabUploadIntegrations(): Promise<LabUploadIntegration[]> {
  return apiClient.get<ApiResponse<LabUploadIntegration[]>>(BASE).then((r) => r.data.data);
}

export function listLabUploadLabTypes(): Promise<LabUploadLabType[]> {
  return apiClient.get<ApiResponse<LabUploadLabType[]>>(`${BASE}/lab-types`).then((r) => r.data.data);
}

export function createLabUploadIntegration(payload: { name: string; noteTypeId: number; canCheck?: boolean }): Promise<{
  integration: LabUploadIntegration;
  credentials: LabUploadCredentials;
}> {
  return apiClient
    .post<ApiResponse<{ integration: LabUploadIntegration; credentials: LabUploadCredentials }>>(BASE, payload)
    .then((r) => r.data.data);
}

export function updateLabUploadIntegration(
  id: number,
  payload: Partial<{ name: string; noteTypeId: number; active: boolean; canCheck: boolean }>,
): Promise<LabUploadIntegration> {
  return apiClient.patch<ApiResponse<LabUploadIntegration>>(`${BASE}/${id}`, payload).then((r) => r.data.data);
}

export function regenerateLabUploadSecret(id: number): Promise<LabUploadCredentials> {
  return apiClient
    .post<ApiResponse<{ credentials: LabUploadCredentials }>>(`${BASE}/${id}/regenerate-secret`)
    .then((r) => r.data.data.credentials);
}

export function listLabUploadRequests(params: { outcome?: LabUploadOutcome | ''; page?: number } = {}): Promise<{
  items: LabUploadRequestRow[];
  total: number;
}> {
  const query: Record<string, string | number> = {};
  if (params.outcome) query.outcome = params.outcome;
  if (params.page) query.page = params.page;
  return apiClient
    .get<ApiResponse<LabUploadRequestRow[]> & { meta?: { total?: number } }>(`${BASE}/requests`, { params: query })
    .then((r) => ({ items: r.data.data, total: Number(r.data.meta?.total ?? r.data.data.length) }));
}

export function getLabUploadReadiness(): Promise<LabUploadReadiness> {
  return apiClient.get<ApiResponse<LabUploadReadiness>>(`${BASE}/readiness`).then((r) => r.data.data);
}
